import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { createPortal } from "react-dom";
import { currentDomain, currentPath } from "../Index";
import { usePromotionsData } from "../data/PromotionData";
import Deposit from "../transactions/Deposit";
import LoginGlobal from "../data/LoginGlobal";
import { useNavigate } from "react-router-dom";
import useAppPromotionTheme from "../hooks/useAppPromotionTheme";

export default function NewMemberPackageBonus({ isLightMode }) {
	const isAppPromo = useAppPromotionTheme(isLightMode);
	const { promotionsData, isPromotionDataReady } = usePromotionsData();
	const data = promotionsData.find(
		(promo) => promo.title === "New Member Bonus",
	);
	const [isMobile, setIsMobile] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isDepositOpen, setIsDepositOpen] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const loggedInState = localStorage.getItem("isLoggedIn") === "true";
		setIsLoggedIn(loggedInState);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 841);
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!isDepositOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isDepositOpen]);

	// If the promo page is expired - throws error
	useEffect(() => {
		if (!isPromotionDataReady) return;

		if (!data) {
			navigate("/not-found");
		}
	}, [isPromotionDataReady, data, navigate]);

	if (!isPromotionDataReady || !data) return null;

	return (
		<>
			<Helmet>
				<meta charSet="utf-8" />
				<title>New Member Bonus</title>
				<link rel="icon" href="/images/fav.webp" sizes="32x32" />
				<meta name="description" content="ddd" />
				<link rel="canonical" href={`${currentDomain}${currentPath}`} />
			</Helmet>
			<div className="promotion-page-main all-page-main-container-min-height">
				<div className="promotion-page-container main-container-global-adjusted-padding">
					<div className="promotion-tnc-page">
						<div className="promotion-page-breadcrumb">
							<a
								href={
									isAppPromo
										? "/promotion/".replace("/promotion", "/app-promotion")
										: "/promotion/"
								}
							>
								Promotions{" "}
							</a>{" "}
							/ <span>{data.title}</span>
						</div>
						<div className="promotion-tnc-banner">
							<img
								src={isMobile ? data.imgSrc : data.bannerSrc}
								alt={`FGH ${data.title}`}
							/>
						</div>
						<div className="promotion-tnc-content">
							<div className="promotion-tnc-info">
								<div
									className={`promotion-indication-message ${data.promotionMessage.class}`}
								>
									{data.promotionMessage.text}
								</div>
								<h1>New Member Bonus</h1>
								<h2>Claim up to ₱1,250 in Bonuses!</h2>
							</div>
							<div className="promotion-tnc-mechanic-content">
								<strong>Terms and Conditions: </strong>
								<div className="table-wrapper">
									<table className="promotion-tnc-table">
										<thead>
											<th>#</th>
											<th>Minimum Deposit</th>
											<th>Bonus %</th>
											<th>Maximum Bonus</th>
											<th>Rollover Requirement</th>
										</thead>
										<tbody>
											<tr>
												<td>1st Deposit</td>
												<td>200</td>
												<td>20%</td>
												<td>100</td>
												<td
													rowSpan={5}
													style={{
														textAlign: "center",
														verticalAlign: "middle",
													}}
												>
													3x - SLOTS only
												</td>
											</tr>
											<tr>
												<td>2nd Deposit</td>
												<td>300</td>
												<td>25%</td>
												<td>150</td>
											</tr>
											<tr>
												<td>3rd Deposit</td>
												<td>400</td>
												<td>30%</td>
												<td>200</td>
											</tr>
											<tr>
												<td>4th Deposit</td>
												<td>500</td>
												<td>35%</td>
												<td>300</td>
											</tr>
											<tr>
												<td>5th Deposit</td>
												<td>1000</td>
												<td>40%</td>
												<td>500</td>
											</tr>
										</tbody>
									</table>
								</div>

								<ol>
									<li>
										This promotion is for new members only. New members are the
										players who have registered since September 1, 2025.
									</li>
									<li>This promotion is FOR SLOTS GAMES ONLY.</li>
									<li>
										Table games, video poker, and fishing games from CQ9 and
										JILI, as well as any other games we believe fall into this
										category, are set to be excluded from this promotion.
									</li>
									<li>
										Wagering Requirement: 3X Rollover Policy (the deposited
										amount plus the bonus amount) before any cash out.
									</li>
									<li>
										If the player loses the deposited amount, including the
										bonus, please contact FGH support immediately to reset the
										remaining rollover requirement.
									</li>
									<li>
										This promotion cannot be combined with any other FGH ongoing
										promotions, bonuses, or offers, unless expressly specified
										otherwise.
									</li>
									<li>
										Select <strong className="highlight">"APPLY BONUS"</strong>{" "}
										in the Deposit or Cash In request to add the bonus to your
										deposited amount.
									</li>
									<li>
										Once applied, the bonus will be credited automatically
										together with your deposited amount.
									</li>
									<li>
										We reserve the right to change, suspend or cancel the
										promotion at any time.
									</li>
								</ol>
								{!isAppPromo && (
									<>
										<div className="promotion-tnc-cta">
											{isLoggedIn ? (
												<button
													type="button"
													className="promotion-cta-btn"
													style={{ cursor: "pointer" }}
													onClick={() => setIsDepositOpen(true)}
												>
													Grab your Bonus
												</button>
											) : (
												<LoginGlobal
													label="Grab your Bonus"
													btnClass="promotion-cta-btn"
													onlyBtn={true}
												/>
											)}
										</div>
										{isDepositOpen &&
											createPortal(
												<div className="modal-portal">
													<Deposit
														onClose={() => setIsDepositOpen(false)}
														defaultBonusName="New Member Package Bonus"
													/>
												</div>,
												document.body,
											)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
